// Copyright (c) 2026 Michael F. Kamprath
//
// bee-spawner.js -- ambient bee fly-by subsystem (slice 2.5.3a).
// Flowers-only.  A 5-frame bee sprite follows a bezier path through
// the theme's `flyingRect`, originating from one of two
// `startingZones` (one off-screen on each edge), visits up to
// `maxVisits` random countable objects, then flies off the
// opposite edge.  A buzz loop plays during flight, alternating
// between `bee-buzz-01.mp3` and `bee-buzz-02.mp3` once per cycle.
//
// Slice 2.5.3a is ambient-only -- no tap handling, no mini-game.
// The bee sprite's `eventMode` is 'none' so clicks fall through to
// the scene beneath.  Slice 2.5.3b flips eventMode to 'static',
// attaches a pointertap handler, starts the FlightOfBumbleBeeRims
// one-shot, and swaps the active path for the straight-line
// escape physics.
//
// Architecture mirrors cloud-spawner.js:
//   - timer-driven spawn loop with a random cooldown between flights
//   - per-flight rAF-driven position + rotation tween
//   - destroy() cancels all timers + in-flight tweens and tears
//     down the bee sprite + audio without leaks
//
// Legacy reference: FlyingObjectManager.m + FlyingObjectView.m +
// CountGameController.m:1036-1056 (startFlyingObjects).

import * as PIXI from 'pixi.js';
import { animateOver } from './entrance.js';
import { playLoop, playCancellable } from '../audio/engine.js';

const DEFAULT_DEPS = Object.freeze({
  setTimeoutImpl: (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl: (id) => clearTimeout(id),
  rng: Math.random,
  now: () => performance.now(),
  // Audio hooks -- DI'd so tests can stub without touching Howler.
  playBuzz: (url) => playLoop(url, { volume: 1 }),
  // Bee touch sound.  Returns a MusicHandle with .stop() and
  // .fadeOut(seconds) so the mini-game can fade it out with a 2 s
  // decay on viewport exit (legacy FlyingObjectManager.m behaviour:
  // fadeSoundOnCancel = YES, fadeDuration = 2.0).
  playTouchSound: (url) => playCancellable(url, { volume: 1 }),
  // Tap callbacks: `onBeeTap` fires on EVERY tap (bee_taps
  // telemetry increment); `onMiniGameStart` fires ONLY on the first
  // tap that transitions ambient -> mini-game (side_game_engaged
  // flip).  Subsequent taps during the same mini-game don't
  // re-fire onMiniGameStart.
  onBeeTap: null,
  onMiniGameStart: null,
});

// Default re-spawn cooldown bounds (in seconds) when themes.json
// doesn't configure them.  Five to twenty seconds gives a visibly
// varied cadence without the bee feeling either spammy or absent.
// Override per theme with `flyingObject.respawnMinSec` /
// `respawnMaxSec` in themes.json.
const DEFAULT_RESPAWN_MIN_SEC = 5;
const DEFAULT_RESPAWN_MAX_SEC = 20;

// Frame-animation rotation helper: map a velocity vector to sprite
// scale.x + rotation so the bee's head (native orientation: left)
// always points along the direction of motion.  Flipping the sprite
// when moving right avoids the upside-down rotation a plain
// atan2-based approach would produce.  See 2.5.3 prompt for the
// user's explicit physics note.
export function __applyFacingForTests(sprite, vx, vy, baseScaleAbs) {
  return applyFacing(sprite, vx, vy, baseScaleAbs);
}
function applyFacing(sprite, vx, vy, baseScaleAbs) {
  // Bee PNG's native head direction is RIGHT (visible eyes on the
  // right side of the texture).  No flip when moving right; flip
  // (scale.x negative) when moving left.
  //
  // IMPORTANT: PIXI applies transforms in the order scale → rotate.
  // When scale.x is negative, the image is first mirrored across
  // the Y axis and THEN rotated -- which reverses the sense of the
  // vertical tilt in screen space.  A rotation that tilts the head
  // DOWN on rightward motion tilts the head UP on leftward motion
  // (and vice versa).  To compensate, we negate the rotation when
  // the sprite is flipped.  Without this negation, the (-1, -1)
  // case (moving up-left) ends up with the head pointing down-left.
  const flipped = vx < 0;
  const horizMag = Math.abs(vx);
  const tiltRaw = (horizMag < 1e-6 && Math.abs(vy) < 1e-6)
    ? 0
    : Math.atan2(vy, horizMag);
  sprite.scale.x = flipped ? -baseScaleAbs : baseScaleAbs;
  sprite.rotation = flipped ? -tiltRaw : tiltRaw;
}

// Sample a cubic bezier B(t) = (1-t)^3 P0 + 3(1-t)^2 t P1 + 3(1-t) t^2 P2 + t^3 P3.
function bezier(t, p0, p1, p2, p3) {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

// Derivative of the above bezier (tangent vector), used to drive
// sprite rotation so the bee faces the direction of motion.
function bezierTangent(t, p0, p1, p2, p3) {
  const mt = 1 - t;
  return {
    x: 3 * mt * mt * (p1.x - p0.x)
       + 6 * mt * t * (p2.x - p1.x)
       + 3 * t * t * (p3.x - p2.x),
    y: 3 * mt * mt * (p1.y - p0.y)
       + 6 * mt * t * (p2.y - p1.y)
       + 3 * t * t * (p3.y - p2.y),
  };
}

// Approximate arc length of a cubic bezier by sampling N points
// and summing chord lengths.  Used only for duration calculation
// (duration = length / speed), so a cheap approximation is fine.
function bezierLength(p0, p1, p2, p3, samples = 12) {
  let last = p0;
  let len = 0;
  for (let i = 1; i <= samples; i++) {
    const p = bezier(i / samples, p0, p1, p2, p3);
    len += Math.hypot(p.x - last.x, p.y - last.y);
    last = p;
  }
  return len;
}

/**
 * @typedef {Object} BeeSpawnerHandle
 * @property {() => void} start
 * @property {() => void} destroy
 * @property {() => boolean} __isEnabled
 * @property {() => boolean} __isFlying
 */

/**
 * Build a bee spawner for the current scene.  `themeData` carries
 * the bee config under `flyingObject` (plus per-variant
 * `flyingRect` / `startingZones` / `flyingSpeed`).  Themes without
 * a `flyingObject` block -- every theme except flowers -- get a
 * no-op handle so callers don't have to branch.
 *
 * @param {{
 *   container: PIXI.Container,
 *   themeData: object,
 *   variant: 'standard' | 'wide',
 *   designW: number,
 *   designH: number,
 *   objects?: Array<{ x: number, y: number, _spriteW?: number, _spriteH?: number }>,
 *   deps?: typeof DEFAULT_DEPS,
 * }} opts
 * @returns {BeeSpawnerHandle}
 *
 * The factory is SYNCHRONOUS -- texture preload happens in the
 * background so the call site (app.js's `buildCurrentScene`) pays
 * zero microtask boundaries beyond its existing cloud-spawner
 * await.  If `start()` is called before the preload finishes, the
 * first flight is deferred until textures are ready; `destroy()`
 * cancels any still-pending preload.
 */
export function createBeeSpawner({
  container,
  themeData,
  variant,
  designW,
  designH,
  objects = [],
  deps: depsOverride,
} = {}) {
  const deps = { ...DEFAULT_DEPS, ...(depsOverride || {}) };

  // --- Short-circuit when theme has no bee config -------------------------
  const cfg = themeData?.flyingObject || null;
  const variantCfg = themeData?.[variant] || {};
  const rectNorm = variantCfg.flyingRect || null;
  const zoneNorms = Array.isArray(variantCfg.startingZones)
    ? variantCfg.startingZones
    : [];
  const speed = Number.isFinite(variantCfg.flyingSpeed)
    ? variantCfg.flyingSpeed
    : 0;

  const enabled = cfg
    && Array.isArray(cfg.imageNames) && cfg.imageNames.length > 0
    && rectNorm && zoneNorms.length >= 2
    && speed > 0;

  if (!enabled) {
    return {
      start() {},
      destroy() {},
      __isEnabled() { return false; },
      __isFlying() { return false; },
    };
  }

  // --- Denormalize geometry into design-pixel coordinates ----------------
  const flyingRect = {
    x: rectNorm.x * designW,
    y: rectNorm.y * designH,
    w: rectNorm.w * designW,
    h: rectNorm.h * designH,
  };
  const zones = zoneNorms.map((z) => ({
    x: z.x * designW,
    y: z.y * designH,
    w: z.w * designW,
    h: z.h * designH,
  }));

  // --- Preload textures + buzz SFX in the background ----------------------
  // `start()` is deferred until `validFrames` is populated.  Failed
  // loads are dropped so a single broken asset doesn't poison the
  // whole flight subsystem.
  let validFrames = [];
  let preloadDone = false;
  let pendingStart = false;
  const frameTextures = [];
  Promise.all(cfg.imageNames.map(async (path, idx) => {
    const url = `/${path}`;
    try {
      PIXI.Assets.add({ alias: url, src: url });
      frameTextures[idx] = await PIXI.Assets.load(url);
    } catch { /* skip broken frame */ }
  })).then(() => {
    if (destroyed) return;
    validFrames = frameTextures.filter(Boolean);
    preloadDone = true;
    if (pendingStart && validFrames.length > 0) {
      scheduleNextAmbientFlight();
    }
  });
  // Frame timing table (absolute seconds from cycle start).  The
  // array is computed up front against cfg.frameOffsetsSec; once the
  // async preload resolves, `validFrames[i]` corresponds to
  // `frameOffsetsSec[i]`.  If some frames failed to load the cycle
  // still works -- `advanceFrame` falls back to the last available
  // index.
  const frameOffsetsSec = Array.isArray(cfg.frameOffsetsSec)
    ? cfg.frameOffsetsSec.slice(0, cfg.imageNames.length)
    : cfg.imageNames.map((_, i) => i * 0.1);
  const lastFrameDurationSec = Number.isFinite(cfg.lastFrameDurationSec)
    ? cfg.lastFrameDurationSec : 0.1;
  const cycleSec = frameOffsetsSec[frameOffsetsSec.length - 1] + lastFrameDurationSec;

  const buzzUrls = Array.isArray(cfg.flyingSoundUrls) ? cfg.flyingSoundUrls : [];
  const touchSoundUrl = typeof cfg.touchSoundUrl === 'string' && cfg.touchSoundUrl
    ? cfg.touchSoundUrl : null;
  const maxVisits = Number.isFinite(cfg.maxVisits) ? cfg.maxVisits : 2;

  // Bee sprite display scale.  Default 1.5 renders the 64-px native
  // texture at 96 design-px -- roughly the same on-screen footprint
  // as a flower's face, so kids have a big-enough target to tap.
  // Override per theme via `flyingObject.displayScale`.
  const baseScaleAbs = Number.isFinite(cfg.displayScale) && cfg.displayScale > 0
    ? cfg.displayScale
    : 1.5;

  // Mini-game velocity bounds (percent of design-pixel viewport
  // per second).  Horizontal is a positive magnitude range; sign is
  // set by the tap's reverse-direction rule.  Vertical is signed in
  // the user's math convention (positive = upward, negative =
  // downward) -- code converts to PIXI's Y-down screen space by
  // negating.
  const horizPctMin = Number.isFinite(cfg.minigameHorizSpeedPctMin)
    ? Math.abs(cfg.minigameHorizSpeedPctMin) : 15;
  const horizPctMax = Math.max(horizPctMin,
    Number.isFinite(cfg.minigameHorizSpeedPctMax)
      ? Math.abs(cfg.minigameHorizSpeedPctMax) : 30);
  const vertPctMin = Number.isFinite(cfg.minigameVertSpeedPctMin)
    ? cfg.minigameVertSpeedPctMin : -20;
  const vertPctMax = Math.max(vertPctMin,
    Number.isFinite(cfg.minigameVertSpeedPctMax)
      ? cfg.minigameVertSpeedPctMax : 30);

  // Re-spawn cooldown bounds: read from themeData.flyingObject and
  // fall back to the module defaults (5 s .. 20 s).  The delay
  // before the next flight is uniform random in [minMs, maxMs].
  const respawnMinMs = Math.max(0,
    (Number.isFinite(cfg.respawnMinSec) ? cfg.respawnMinSec : DEFAULT_RESPAWN_MIN_SEC) * 1000);
  const respawnMaxMsRaw = Math.max(0,
    (Number.isFinite(cfg.respawnMaxSec) ? cfg.respawnMaxSec : DEFAULT_RESPAWN_MAX_SEC) * 1000);
  const respawnMaxMs = Math.max(respawnMinMs, respawnMaxMsRaw);
  function pickRespawnDelayMs() {
    return respawnMinMs + deps.rng() * (respawnMaxMs - respawnMinMs);
  }

  // --- Runtime state ------------------------------------------------------
  let destroyed = false;
  let started = false;
  let respawnTimerId = null;
  let buzzHandle = null;
  let buzzIndex = 0;
  let activeCancel = null;    // cancel token for the current flight tween
  let activeSprite = null;    // the single bee sprite in flight
  let flying = false;

  // Phase state machine.
  //   'ambient'  : bezier flight; taps trigger transition to mini-game.
  //   'minigame' : straight-line physics; taps redirect the bee.
  //   'idle'     : no bee on screen; waiting for respawn timer to fire.
  const Phase = Object.freeze({ AMBIENT: 'ambient', MINIGAME: 'minigame', IDLE: 'idle' });
  let phase = Phase.IDLE;

  // Mini-game velocity (design-px / sec).  Updated at tap time so
  // the runMiniGame loop reads the latest values on each leg restart.
  let miniGameVelocity = { x: 0, y: 0 };

  // Sampler set by the ambient bezier tween so the tap handler can
  // read the current motion direction at tap time (needed to
  // reverse horizontal direction on mini-game entry).
  let currentTangentSampler = null;

  // Handle to the currently-playing touch sound (MusicHandle).
  // Null when no mini-game is active.  stored at mini-game start
  // and faded out on viewport exit or destroyed on teardown.
  let touchSoundHandle = null;

  function stopBuzz() {
    if (buzzHandle && typeof buzzHandle.stop === 'function') {
      try { buzzHandle.stop(); } catch { /* no-op */ }
    }
    buzzHandle = null;
  }

  function startBuzz() {
    if (!buzzUrls.length) return;
    const url = `/${buzzUrls[buzzIndex % buzzUrls.length]}`;
    buzzIndex += 1;
    try { buzzHandle = deps.playBuzz(url); }
    catch { buzzHandle = null; }
  }

  /**
   * Pick 0..maxVisits random countable objects to guide the
   * bezier via-points.  Results are in design-px coordinates at
   * each object's visual center.
   */
  function pickVisitPoints() {
    if (!objects.length) return [];
    const count = Math.min(
      maxVisits,
      Math.max(0, Math.floor(deps.rng() * (maxVisits + 1))),
    );
    if (count === 0) return [];
    const pool = objects.slice();
    // Fisher-Yates partial shuffle to pick `count` distinct objects.
    for (let i = 0; i < count && pool.length > 1; i++) {
      const j = i + Math.floor(deps.rng() * (pool.length - i));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, count).map((o) => ({
      x: o.x + (o._spriteW ?? 0) / 2,
      y: o.y + (o._spriteH ?? 0) / 2,
    }));
  }

  /**
   * Pick a random point inside a rectangle (design-px coords).
   */
  function pickPointIn(rect) {
    return {
      x: rect.x + deps.rng() * rect.w,
      y: rect.y + deps.rng() * rect.h,
    };
  }

  /**
   * Plan a flight: pick a starting zone on one edge, exit on the
   * opposite edge, and thread the bezier through 0..maxVisits
   * visit points.  Returns the 4 control points for a cubic
   * bezier approximation.  When visits > 0 the middle control
   * points are biased toward the visit points so the path
   * visually "dips" to them.
   */
  function planFlight() {
    const startZoneIdx = Math.floor(deps.rng() * zones.length) % zones.length;
    const endZoneIdx = (startZoneIdx + 1) % zones.length;
    const p0 = pickPointIn(zones[startZoneIdx]);
    const p3 = pickPointIn(zones[endZoneIdx]);
    const visits = pickVisitPoints();

    // Control-point placement: with 2 visits, use them directly.
    // With 1 visit, dampen toward flyingRect midline.  With 0,
    // derive controls from p0/p3 + rect midline so the path isn't
    // a straight line (looks more organic).
    let p1, p2;
    if (visits.length >= 2) {
      p1 = visits[0];
      p2 = visits[1];
    } else if (visits.length === 1) {
      const mid = visits[0];
      p1 = { x: (p0.x * 2 + mid.x) / 3, y: (p0.y * 2 + mid.y) / 3 };
      p2 = { x: (p3.x * 2 + mid.x) / 3, y: (p3.y * 2 + mid.y) / 3 };
    } else {
      const midY = flyingRect.y + flyingRect.h / 2;
      p1 = { x: p0.x + (p3.x - p0.x) / 3, y: midY + (deps.rng() - 0.5) * flyingRect.h * 0.3 };
      p2 = { x: p0.x + 2 * (p3.x - p0.x) / 3, y: midY + (deps.rng() - 0.5) * flyingRect.h * 0.3 };
    }
    return { p0, p1, p2, p3 };
  }

  /**
   * Build the bee sprite at frame 0 and add it to the container.
   * Anchor at (0.5, 0.5) so the rotation pivot is the bee's body
   * center and position assignments are the visual center point.
   */
  function createSprite() {
    const sprite = new PIXI.Sprite(validFrames[0]);
    sprite.anchor.set(0.5, 0.5);
    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    sprite.on('pointertap', () => onBeeTapped(sprite));
    sprite.scale.set(baseScaleAbs);
    sprite.__frameStartMs = deps.now();
    sprite.__validFrames = validFrames;
    return sprite;
  }

  /**
   * Phase-aware tap handler.  Every tap fires `onBeeTap` (bee_taps
   * telemetry).  The FIRST tap during ambient flight also fires
   * `onMiniGameStart` (side_game_engaged) and transitions the
   * spawner into mini-game phase.  Subsequent taps during the same
   * mini-game only redirect the bee.
   */
  function onBeeTapped(sprite) {
    if (destroyed) return;
    try { deps.onBeeTap?.(); } catch { /* non-fatal */ }

    if (phase === Phase.AMBIENT) {
      // Ambient -> Mini-game transition.
      try { deps.onMiniGameStart?.(); } catch { /* non-fatal */ }

      // Sample the current tangent so we know which horizontal
      // direction to REVERSE from.  Default to +x if no sampler
      // (shouldn't happen in practice).
      const tan = currentTangentSampler ? currentTangentSampler() : { x: 1, y: 0 };
      const currentHorizDir = tan.x >= 0 ? 1 : -1;
      setMiniGameVelocityFromTap(-currentHorizDir);

      phase = Phase.MINIGAME;
      // Cancel the bezier tween; runOneFlight's post-tween branch
      // detects phase=MINIGAME and hands off to runMiniGame(sprite)
      // instead of destroying the sprite.
      if (activeCancel) activeCancel.cancelled = true;
      return;
    }

    if (phase === Phase.MINIGAME) {
      // Redirect: reverse horizontal, pick fresh vertical.
      const currentHorizDir = miniGameVelocity.x >= 0 ? 1 : -1;
      setMiniGameVelocityFromTap(-currentHorizDir);
      // Cancel the current straight-line leg so runMiniGame's loop
      // picks up the new velocity on the next iteration.
      if (activeCancel) activeCancel.cancelled = true;
    }
    // phase === IDLE: bee not on screen; tap ignored.
  }

  /**
   * Compute a fresh mini-game velocity vector.  The caller passes
   * the sign of the new horizontal direction (-1 or +1); the
   * magnitude is a uniform random value in
   * `[minigameHorizSpeedPctMin, minigameHorizSpeedPctMax]` of
   * `designW` per second.  The vertical component is a uniform
   * signed random value in `[minigameVertSpeedPctMin, maxPctMax]`
   * of `designH` per second, interpreted in MATH convention
   * (positive = upward) -- we negate at assignment time to match
   * PIXI's Y-down screen convention.
   */
  function setMiniGameVelocityFromTap(horizSign) {
    const horizPct = horizPctMin + deps.rng() * (horizPctMax - horizPctMin);
    const horizSpeedPx = (horizPct / 100) * designW;
    const vx = horizSpeedPx * horizSign;

    const vertPct = vertPctMin + deps.rng() * (vertPctMax - vertPctMin);
    const vertSpeedPxMath = (vertPct / 100) * designH;   // +up in math
    const vy = -vertSpeedPxMath;                         // -up in screen

    miniGameVelocity = { x: vx, y: vy };
  }

  function advanceFrame(sprite) {
    if (!cycleSec) return;
    const elapsedSec = ((deps.now() - sprite.__frameStartMs) / 1000) % cycleSec;
    // Pick the last frame whose offset <= elapsedSec.
    let idx = 0;
    for (let i = 0; i < frameOffsetsSec.length; i++) {
      if (frameOffsetsSec[i] <= elapsedSec) idx = i;
      else break;
    }
    const tex = validFrames[idx];
    if (tex && sprite.texture !== tex) sprite.texture = tex;
  }

  /**
   * Run one ambient flight: plan the bezier, build the sprite,
   * tween along the bezier while cycling frames + facing direction.
   * If a tap triggers the mini-game mid-flight, the bezier tween
   * is cancelled and this function hands the sprite off to
   * runMiniGame() rather than destroying it.  Otherwise (natural
   * completion or destroy), the sprite is torn down and the next
   * ambient flight is scheduled.
   */
  async function runOneFlight() {
    if (destroyed) return;
    const { p0, p1, p2, p3 } = planFlight();
    const length = bezierLength(p0, p1, p2, p3);
    const durationMs = Math.max(100, (length / speed) * 1000);

    const sprite = createSprite();
    sprite.x = p0.x;
    sprite.y = p0.y;
    const tan0 = bezierTangent(0, p0, p1, p2, p3);
    applyFacing(sprite, tan0.x, tan0.y, baseScaleAbs);
    container.addChild(sprite);
    activeSprite = sprite;

    const cancel = { cancelled: false };
    activeCancel = cancel;
    flying = true;
    phase = Phase.AMBIENT;
    startBuzz();

    // Install a tangent sampler so the tap handler can read the
    // current motion direction at the instant of tap.  `currentT`
    // is updated on every onFrame call.
    let currentT = 0;
    currentTangentSampler = () => bezierTangent(currentT, p0, p1, p2, p3);

    await animateOver(durationMs, (t) => {
      if (!sprite.parent || destroyed) return;
      currentT = t;
      const p = bezier(t, p0, p1, p2, p3);
      sprite.x = p.x;
      sprite.y = p.y;
      const tan = bezierTangent(t, p0, p1, p2, p3);
      applyFacing(sprite, tan.x, tan.y, baseScaleAbs);
      advanceFrame(sprite);
    }, cancel);

    currentTangentSampler = null;

    // Tap transitioned us into the mini-game?  Hand off the sprite
    // (it stays on screen; runMiniGame manages its lifecycle).
    if (!destroyed && phase === Phase.MINIGAME) {
      // Stop the buzz -- mini-game replaces it with the touch sound.
      stopBuzz();
      await runMiniGame(sprite);
      return;
    }

    // Normal ambient completion (bezier finished or destroy hit).
    try { sprite.parent?.removeChild(sprite); } catch { /* no-op */ }
    try { sprite.destroy(); } catch { /* no-op */ }
    if (activeSprite === sprite) activeSprite = null;
    if (activeCancel === cancel) activeCancel = null;
    flying = false;
    phase = Phase.IDLE;
    stopBuzz();

    if (destroyed) return;
    scheduleNextAmbientFlight();
  }

  /**
   * Straight-line physics phase.  Each "leg" runs as one
   * long-duration animateOver; the onFrame integrates position
   * from the leg's start + velocity * elapsed.  A tap during the
   * mini-game cancels the current leg (the tap handler updated
   * `miniGameVelocity` to the new direction) and the while loop
   * starts a new leg with the updated velocity.  The mini-game
   * ends when the bee's position crosses the viewport edge +/- a
   * half-sprite margin; the touch sound fades out over 2 s and the
   * sprite is despawned.
   */
  async function runMiniGame(sprite) {
    // Play the touch sound one-shot with a cancellable handle.
    if (touchSoundUrl) {
      try { touchSoundHandle = deps.playTouchSound?.(`/${touchSoundUrl}`) ?? null; }
      catch { touchSoundHandle = null; }
    }

    let exited = false;
    // Leg loop: each iteration runs until either the bee exits the
    // viewport (exited=true) or destroy() flips the cancel token
    // without exited=true (which we detect and break).
    while (!destroyed && !exited) {
      const cancel = { cancelled: false };
      activeCancel = cancel;
      const startX = sprite.x;
      const startY = sprite.y;
      const { x: vx, y: vy } = miniGameVelocity;
      applyFacing(sprite, vx, vy, baseScaleAbs);

      // 1_000_000 ms = ~16 minutes; plenty of runway.  The leg
      // ends via cancel long before the tween's natural duration.
      await animateOver(1_000_000, (_, elapsedMs) => {
        if (destroyed || cancel.cancelled) return;
        const elapsedSec = elapsedMs / 1000;
        sprite.x = startX + vx * elapsedSec;
        sprite.y = startY + vy * elapsedSec;
        advanceFrame(sprite);
        // Viewport exit -- margin = half sprite size so the bee
        // fully leaves rather than clipping at the edge.
        const halfW = (sprite.width || 0) / 2;
        const halfH = (sprite.height || 0) / 2;
        if (sprite.x < -halfW || sprite.x > designW + halfW
            || sprite.y < -halfH || sprite.y > designH + halfH) {
          exited = true;
          cancel.cancelled = true;
        }
      }, cancel);
    }

    // Fade out the touch sound on exit.  fadeOut returns a
    // Promise; we kick it off fire-and-forget so teardown doesn't
    // block on the full 2 s decay.
    if (touchSoundHandle) {
      const h = touchSoundHandle;
      touchSoundHandle = null;
      try { h.fadeOut(2.0); } catch { /* no-op */ }
    }

    // Despawn the bee.
    try { sprite.parent?.removeChild(sprite); } catch { /* no-op */ }
    try { sprite.destroy(); } catch { /* no-op */ }
    if (activeSprite === sprite) activeSprite = null;
    if (activeCancel) activeCancel = null;
    flying = false;
    phase = Phase.IDLE;

    if (destroyed) return;
    scheduleNextAmbientFlight();
  }

  function scheduleNextAmbientFlight() {
    respawnTimerId = deps.setTimeoutImpl(() => {
      respawnTimerId = null;
      runOneFlight();
    }, pickRespawnDelayMs());
  }

  function start() {
    if (started || destroyed) return;
    started = true;
    // If the texture preload is still in flight, defer the first
    // spawn until it resolves.  The preload-complete handler
    // above checks `pendingStart` and picks up from here.
    if (!preloadDone) { pendingStart = true; return; }
    if (validFrames.length === 0) return;
    // Uses the same cooldown distribution as subsequent flights so
    // the bee doesn't pop in the instant the entrance finishes.
    scheduleNextAmbientFlight();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (respawnTimerId !== null) {
      try { deps.clearTimeoutImpl(respawnTimerId); } catch { /* no-op */ }
      respawnTimerId = null;
    }
    if (activeCancel) activeCancel.cancelled = true;
    activeCancel = null;
    if (activeSprite) {
      try { activeSprite.parent?.removeChild(activeSprite); } catch { /* no-op */ }
      try { activeSprite.destroy(); } catch { /* no-op */ }
      activeSprite = null;
    }
    stopBuzz();
    // Mini-game touch sound: hard stop on destroy (no fade, scene
    // is being torn down -- caller wants silence immediately).
    if (touchSoundHandle) {
      try { touchSoundHandle.stop(); } catch { /* no-op */ }
      touchSoundHandle = null;
    }
    currentTangentSampler = null;
    flying = false;
    phase = Phase.IDLE;
  }

  return {
    start,
    destroy,
    __isEnabled() { return true; },
    __isFlying() { return flying; },
    __getPhase() { return phase; },
  };
}
